import { Suspense, useCallback, useRef, useState } from "react";
import { NavLink, Outlet, Route, Routes, useParams } from "react-router-dom";
import styled from "styled-components";
import { Button } from "@mui/material";
import { AddCircle as AddCircleIcon } from "@mui/icons-material";
import { MapContainer, Pane, ScaleControl } from "react-leaflet";
import { useTranslation } from "react-i18next";

import { useBounds } from "src/hooks";
import { useCase } from "src/hooks/useCase";
import { CaseLayout } from "src/layouts/CaseLayout";

import { CaseGeneralSettingsPage } from "./Case/CaseGeneralSettingsPage";
import { GridSettingsPage } from "./Case/GridSettingsPage";
import { RegularGridPage } from "./Case/GridSettings/RegularGridPage";
import { LineBasedGridPage } from "./Case/GridSettings/LineBasedGridPage";
import { PointBasedGridPage } from "./Case/GridSettings/PointBasedGridPage";
import { EmissionsPage } from "./Case/EmissionsPage";
import { PoisPage } from "./Case/PoisPage";
import { PoiPage } from "./Case/PoiPage";
import { PoiImportPage } from "./Case/PoiImportPage";
import { TrafficEmissionPage } from "./Case/TrafficEmissionPage";
import { TrafficEmissionImportPage } from "./Case/TrafficEmissionImportPage";
import { TrafficEmissionSegmentPage } from "./Case/TrafficEmissionSegmentPage";
import { PointSourceEmissionPage } from "./Case/PointSourceEmissionPage";
import { PointSourceEmissionImportPage } from "./Case/PointSourceEmissionImportPage";
import { PointSourceEmissionPointSourcePage } from "./Case/PointSourceEmissionPointSourcePage";
import { LEZsPage } from "./Case/LEZsPage";
import { LEZPage } from "./Case/LEZPage";
import { FleetConfigPage } from "./Case/FleetConfigPage";
import { ScenarioGeneralSettingsPage } from "./Case/ScenarioGeneralSettingsPage";

import { CoordinatesControl } from "src/components/map/CoordinatesControl";
import { MapZoomControls } from "src/components/map/MapZoomControls";
import { MapSizeObserver } from "src/components/map/MapSizeObserver";
import { PaneLoader } from "src/components/loaders/PaneLoader";
import { CaseNavigation } from "src/components/CaseNavigation";
import { ScenarioList } from "src/components/ScenarioList";
import { CaseStatusButton } from "src/components/CaseStatusButton";
import { IsEnabled } from "src/components/permissions";
import { MapControlBase } from "src/components/map/MapControlBase";
import { BaselayerOnMap } from "src/components/on-map/BaselayerOnMap";
import { ActiveOverlayersOnMap } from "src/components/on-map/ActiveOverlayersOnMap";
import { OverlayersLegend } from "src/components/OverlayersLegend";
import { ResidentialZonesPage } from "./Case/ResidentialZonesPage";
import { ResidentialZonePage } from "./Case/ResidentialZonePage";
import { ResidentialZoneRestrictionsPage } from "./Case/ResidentialZoneRestrictionsPage";
import { LayerManagerOnMap } from "src/components/LayerManager/LayerManagerOnMap";
import { CaseInProgressModal } from "src/components/modals/CaseInProgressModal";
import { SearchControl } from "src/components/map/SearchControl";

export function CasePage() {
  const { t } = useTranslation();
  const { caseId = "" } = useParams<{ caseId: string }>();

  const bounds = useBounds(caseId);
  const { data: acase } = useCase(caseId);

  // const [map, setMap] = useState<LeafletMap | null>(null);
  const [control, setControl] = useState<HTMLDivElement | null>(null);
  console.log(acase);
  const mapRef = useRef(null);

  /**
   * Callbacks
   */
  // TODO: refactor into useRef?
  const handleRef = useCallback((instance: HTMLDivElement) => setControl(instance), [setControl]);

  return render();

  function render() {
    if (!acase) return null;

    return (
      <CaseLayout toolbarChildren={<CaseStatusButton caseId={caseId} />}>
        {acase.status === "CALCULATING" && <CaseInProgressModal />}

        <Styles>
          {/* Left primary panel orienting from the map */}
          <div className="navigation">
            <CaseNavigation className="case-navigation" caseId={caseId} />

            <Suspense fallback={t<string>("label.loading")}>
              <ScenarioList className="scenario-navigation" />
            </Suspense>

            <IsEnabled permission={acase.permissions.createScenario}>
              <Button
                startIcon={<AddCircleIcon />}
                color="primary"
                component={NavLink}
                to={`/cases/${acase.id}/scenarios/new`}
              >
                {t("label.addScenario")}
              </Button>
            </IsEnabled>
          </div>

          {/* Left secondary panel orienting from the map */}
          {mapRef.current && (
            <Suspense fallback={<PaneLoader message={t("label.loading")} />}>
              <Routes>
                <Route path="*" element={<Outlet />}>
                  <Route path="grid-settings/*" element={<GridSettingsPage map={mapRef.current} />} />
                  <Route path="pois" element={<Outlet />}>
                    <Route index element={<PoisPage map={mapRef.current} controlContainer={control} />} />
                    <Route path=":poiId" element={<PoisPage map={mapRef.current} controlContainer={control} />} />
                  </Route>
                  <Route path="scenarios/:scenarioId" element={<Outlet />}>
                    <Route path="emissions" element={<Outlet />}>
                      <Route index element={<EmissionsPage map={mapRef.current} />} />
                      <Route path="traffic" element={<Outlet />}>
                        <Route index element={<EmissionsPage map={mapRef.current} />} />
                        <Route path=":emissionId" element={<Outlet />}>
                          <Route
                            index
                            element={<TrafficEmissionPage map={mapRef.current} controlContainer={control} />}
                          />
                          <Route
                            path="segments/:segmentId"
                            element={<TrafficEmissionPage map={mapRef.current} controlContainer={control} />}
                          />
                        </Route>
                      </Route>
                      <Route path="point-source" element={<Outlet />}>
                        <Route index element={<EmissionsPage map={mapRef.current} />} />
                        <Route path=":emissionId" element={<Outlet />}>
                          <Route
                            index
                            element={<PointSourceEmissionPage map={mapRef.current} controlContainer={control} />}
                          />
                          <Route
                            path="point-sources/:pointSourceId"
                            element={<PointSourceEmissionPage map={mapRef.current} controlContainer={control} />}
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route path={"lezs"} element={<LEZsPage map={mapRef.current} />}>
                      <Route index element={<LEZsPage map={mapRef.current} />} />
                      <Route path=":lezId" element={<LEZsPage map={mapRef.current} />} />
                    </Route>
                    <Route path={"residential-zones"} element={<Outlet />}>
                      <Route index element={<ResidentialZonesPage map={mapRef.current} />} />
                      <Route path=":residentialZoneId" element={<ResidentialZonesPage map={mapRef.current} />} />
                    </Route>
                  </Route>

                  {/* // TODO: add on page loader? */}
                  {/* <Suspense fallback={null}> */}
                  {/* </Suspense> */}
                </Route>
              </Routes>
            </Suspense>
          )}

          <MapContainer
            ref={mapRef}
            bounds={bounds}
            boxZoom={false}
            zoomControl={false}
            className="map-container"
            preferCanvas
          >
            <Pane name="drawing-pane" style={{ zIndex: 9998 }} />
            <Pane name="drawing-pane-vertex" style={{ zIndex: 9999 }} />
            <SearchControl position="topleft" />
            <MapSizeObserver />
            <ScaleControl position="bottomleft" imperial={false} />
            <MapControlBase position="bottomleft">
              <CoordinatesControl />
            </MapControlBase>
            <MapControlBase position="bottomleft">
              <OverlayersLegend context="case-edit" />
            </MapControlBase>

            <MapZoomControls position="bottomright" />

            <LayerManagerOnMap context="case-edit" />
            <MapControlBase position="topleft">
              <div className="mapcontrol-top-left" ref={handleRef}></div>
            </MapControlBase>

            <BaselayerOnMap />
            <ActiveOverlayersOnMap context="case-edit" />
          </MapContainer>

          {/* Right panel orienting from the map */}
          {mapRef.current && (
            <Suspense fallback={<PaneLoader message={t("label.loading")} />}>
              <Routes>
                <Route path="*" element={<Outlet />}>
                  <Route path="general-settings" element={<CaseGeneralSettingsPage />} />
                  <Route path="grid-settings" element={<Outlet />}>
                    <Route path="regular-grid" element={<RegularGridPage map={mapRef.current} />} />
                    <Route path="line-based-grid" element={<LineBasedGridPage />} />
                    <Route path="point-based-grid" element={<PointBasedGridPage />} />
                  </Route>
                  <Route path="pois" element={<Outlet />}>
                    <Route path="import" element={<PoiImportPage />} />
                    <Route path=":poiId" element={<PoiPage map={mapRef.current} />} />
                  </Route>
                  <Route path="scenarios/:scenarioId" element={<Outlet />}>
                    <Route index element={<ScenarioGeneralSettingsPage />} />
                    <Route path="emissions" element={<Outlet />}>
                      <Route path="traffic/:emissionId" element={<Outlet />}>
                        <Route
                          path="segments/:segmentId"
                          element={<TrafficEmissionSegmentPage map={mapRef.current} />}
                        />
                        <Route path="import" element={<TrafficEmissionImportPage />} />
                      </Route>
                      <Route path="point-source/:emissionId" element={<Outlet />}>
                        <Route path="import" element={<PointSourceEmissionImportPage />} />
                        <Route
                          path="point-sources/:pointSourceId"
                          element={<PointSourceEmissionPointSourcePage map={mapRef.current} />}
                        />
                      </Route>
                    </Route>
                    <Route path="residential-zones/:residentialZoneId" element={<Outlet />}>
                      <Route index element={<ResidentialZonePage map={mapRef.current} />} />
                      <Route path="restrictions" element={<ResidentialZoneRestrictionsPage />} />
                    </Route>
                    <Route path="lezs/:lezId" element={<Outlet />}>
                      <Route index element={<LEZPage map={mapRef.current} />} />
                      <Route path="fleet-config" element={<FleetConfigPage />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          )}
        </Styles>
      </CaseLayout>
    );
  }
}

const Styles = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  flex: 1;
  overflow: auto;

  .list-sub-header {
    font-weight: 300;
    color: #3fabff;
    background-color: transparent;
  }

  .navigation {
    position: relative;
    width: ${({ theme }) => theme.caseDrawer.width}px;
    flex-shrink: 0;
    padding: ${({ theme }) => theme.spacing(3, 0)};
    border-right: 1px solid #e9e9e9;
    flex: 0 0 300px;
    overflow: auto;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
  }

  .case-navigation {
    padding: ${({ theme }) => theme.spacing(0, 3)};
  }
  .scenario-navigation {
    padding: 0;
  }

  .active {
    .MuiListItemText-primary {
      font-weight: 800;
    }
  }

  .map-container,
  .leaflet-container {
    width: 100%;
    height: 100%;
    flex-grow: 1;
    user-select: none;
  }
`;
